// Builds calendar deep-links + an .ics for a scheduled item. Every option is
// zero-setup: opening a link lets the user save the event to whichever Google
// / Microsoft account they're signed into — no API keys, OAuth or linking.

function pad(n) {
  return String(n).padStart(2, "0");
}

// UTC basic format: YYYYMMDDTHHMMSSZ.
function fmtUTC(d) {
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function resolveTimes(startISO, endISO, durationMinutes = 60) {
  const start = startISO ? new Date(startISO) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  let end = endISO ? new Date(endISO) : null;
  if (!end || Number.isNaN(end.getTime()) || end <= start) {
    end = new Date(start.getTime() + durationMinutes * 60000);
  }
  return { start, end };
}

export function googleCalendarUrl({ title, startISO, endISO, details, location, durationMinutes = 60 }) {
  const t = resolveTimes(startISO, endISO, durationMinutes);
  if (!t) return null;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: (title || "Fire door appointment").slice(0, 200),
    dates: `${fmtUTC(t.start)}/${fmtUTC(t.end)}`,
  });
  if (details) params.set("details", details);
  if (location) params.set("location", location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function outlookCompose(base, { title, startISO, endISO, details, location, durationMinutes = 60 }) {
  const t = resolveTimes(startISO, endISO, durationMinutes);
  if (!t) return null;
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: (title || "Fire door appointment").slice(0, 200),
    startdt: t.start.toISOString(),
    enddt: t.end.toISOString(),
  });
  if (details) params.set("body", details);
  if (location) params.set("location", location);
  return `${base}/calendar/0/deeplink/compose?${params.toString()}`;
}

// Microsoft 365 work/school accounts.
export function microsoft365Url(ev) {
  return outlookCompose("https://outlook.office.com", ev);
}
// Personal Outlook.com accounts.
export function outlookLiveUrl(ev) {
  return outlookCompose("https://outlook.live.com", ev);
}

function icsEscape(s) {
  return String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

// A downloadable VCALENDAR string (opens in Outlook desktop, Apple Calendar…).
export function buildIcs({ title, startISO, endISO, details, location, durationMinutes = 60 }) {
  const t = resolveTimes(startISO, endISO, durationMinutes);
  if (!t) return null;
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@fdcheck`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//FD Check//Schedule//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${fmtUTC(new Date())}`,
    `DTSTART:${fmtUTC(t.start)}`,
    `DTEND:${fmtUTC(t.end)}`,
    `SUMMARY:${icsEscape(title || "Fire door appointment")}`,
  ];
  if (details) lines.push(`DESCRIPTION:${icsEscape(details)}`);
  if (location) lines.push(`LOCATION:${icsEscape(location)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

// Maps a schedule item (from GET /schedule) to calendar-event fields.
export function scheduleItemToEvent(it, kindLabel) {
  const label = kindLabel || "Appointment";
  const loc = it.building_name || it.location_address || "";
  const title = `${label}${loc ? ` — ${loc}` : ""}`;
  const lines = [];
  if (it.title && it.title !== loc && it.title !== title) lines.push(it.title);
  if (it.assignee_name) lines.push(`Assigned to: ${it.assignee_name}`);
  if (it.door_reference) lines.push(`Door: ${it.door_reference}`);
  if (typeof it.door_count === "number") {
    lines.push(it.door_count > 0 ? `${it.door_count} door${it.door_count === 1 ? "" : "s"} booked` : "Whole building");
  }
  if (it.priority && it.priority !== "normal") lines.push(`Priority: ${it.priority}`);
  lines.push("Added from FD Check");
  return {
    title,
    startISO: it.scheduled_for,
    endISO: it.scheduled_end || null,
    details: lines.join("\n"),
    location: loc,
  };
}
